import React from 'react';
import { useTheme } from '@mui/material/styles';
import { LineChart, MarkElement } from '@mui/x-charts/LineChart';
import type { MarkElementProps } from '@mui/x-charts/LineChart';

export interface WeightChartProps {
  dates: string[];
  weights: (number | null)[];
  yAxisMin?: number;
  yAxisMax?: number;
  width?: number;
  height?: number | string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  labelFontSize?: number;
}

const MARK_SCALE = 1.0;

const WeightChartMark = ({ x, y, color, ...props }: MarkElementProps) => (
  <g transform={`translate(${x}, ${y}) scale(${MARK_SCALE})`}>
    <MarkElement {...props} x={0} y={0} color={color} style={{ fill: color }} />
  </g>
);

// Evenly-spaced tick values (inclusive of min & max).
const buildTicks = (min: number, max: number, count = 5): number[] => {
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + step * i);
};

// A linear 0–N x-axis puts grid lines at each day's centre plus a closing line at the right edge.
const buildDayCentres = (dayCount: number): number[] =>
  Array.from({ length: dayCount }, (_, index) => index + 0.5);

// With no readings, an unlabelled week and fixed y range still draw the grid.
const EMPTY_DAYS = 7;
const EMPTY_WEIGHTS: null[] = buildDayCentres(EMPTY_DAYS).map(() => null);
const EMPTY_Y_MIN = 0;
const EMPTY_Y_MAX = 200;
const hideLabel = () => '';

export const WeightChart = ({
  dates,
  weights,
  yAxisMin,
  yAxisMax,
  width,
  height = '100%',
  margin,
  labelFontSize = 12,
}: WeightChartProps) => {
  const theme = useTheme();
  const isEmpty = !weights.some((weight) => weight != null);
  const yMin = isEmpty ? EMPTY_Y_MIN : yAxisMin;
  const yMax = isEmpty ? EMPTY_Y_MAX : yAxisMax;
  const hiddenTickLabels = isEmpty ? { valueFormatter: hideLabel } : {};
  const dayCount = isEmpty ? EMPTY_DAYS : dates.length;
  const xData = buildDayCentres(dayCount);
  const xAxisScale = {
    scaleType: 'linear' as const,
    data: xData,
    min: 0,
    max: dayCount,
    tickInterval: [...xData, dayCount],
    valueFormatter: isEmpty ? hideLabel : (value: number) => dates[value - 0.5] ?? '',
  };

  return (
    <LineChart
      width={width}
      height={typeof height === 'number' ? height : undefined}
      tooltip={isEmpty ? { trigger: 'none' } : undefined}
      axisHighlight={isEmpty ? { x: 'none', y: 'none' } : undefined}
      sx={{
        height,
        width: '100%',
        ...(isEmpty && {
          '& .MuiChartsGrid-line': {
            stroke: theme.palette.brandBackgroundSecondary.main,
          },
        }),
        // Axis lines (different color from the labels); needs the axis root class to beat MUI's default.
        '& .MuiChartsAxis-root .MuiChartsAxis-line': {
          stroke: theme.palette.grey[500],
        },
        // Tick marks (if you re-enable them via tickSize)
        '& .MuiChartsAxis-tick': {
          stroke: theme.palette.grey[400],
        },
        // Tick labels — this wins alongside tickLabelStyle.fill
        '& .MuiChartsAxis-tickLabel': {
          fill: theme.palette.grey[800],
        },
        '& .MuiAreaElement-root': {
          fill: theme.palette.grey[400],
          fillOpacity: 0.7,
        },
        '& .MuiMarkElement-root': {
          fill: theme.palette.primary.main,
          stroke: theme.palette.primary.main,
        },
      }}
      series={[
        {
          data: isEmpty ? EMPTY_WEIGHTS : weights,
          color: theme.palette.primary.main,
          curve: 'linear',
          area: true,
          baseline: 'min',
          showMark: true,
          connectNulls: true,
          valueFormatter: (value) => (value === null ? null : `${value} lbs`),
        },
      ]}
      xAxis={[
        {
          ...xAxisScale,
          tickSize: 0,
          tickLabelStyle: {
            fontFamily: theme.typography.fontFamily,
            fontWeight: 400,
            fontSize: labelFontSize,
            lineHeight: '16px',
            letterSpacing: 0,
            fill: theme.palette.grey[800],
            transform: 'translateY(10px)',
          },
        },
      ]}
      yAxis={[
        {
          min: yMin,
          max: yMax,
          ...(yMin != null && yMax != null ? { tickInterval: buildTicks(yMin, yMax, 5) } : {}),
          tickSize: 0,
          ...hiddenTickLabels,
          tickLabelStyle: {
            fontFamily: theme.typography.fontFamily,
            fontWeight: 400,
            fontSize: labelFontSize,
            lineHeight: '16px',
            letterSpacing: 0,
            fill: theme.palette.grey[800],
            transform: 'translate(-10px, 0)',
          },
        },
      ]}
      grid={{ horizontal: true, vertical: true }}
      margin={margin ?? { top: 16, right: 0, bottom: 16, left: 24 }}
      slots={{ mark: WeightChartMark }}
      slotProps={{
        mark: { shape: 'circle' },
        area: { fill: theme.palette.grey[400], filter: 'none', opacity: 0.5 },
        legend: { hidden: true },
      }}
    />
  );
};
